import { useI18n } from '@ecom/i18n';
import {
  Badge,
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useAccountSessionsQuery, useRevokeSessionsMutation } from '../api';
import { SESSION_REASON_LABEL_KEY } from '../types';

import { PasswordActionDialog } from './PasswordActionDialog';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useSession } from '@/lib/session';

/**
 * The sessions of ONE account - when, from which address, with which browser - and "end them all". The address and the browser are
 * personal data kept 90 days: past that the API answers nothing and this says so. Ending the sessions of one's own account is not
 * offered (the database refuses it too).
 */
export function AccountSessionsPanel({
  userId,
  label,
  reason,
}: {
  readonly userId: string;
  readonly label: string;
  readonly reason: string;
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const { userId: me } = useSession();
  const query = useAccountSessionsQuery(userId, reason);
  const mutation = useRevokeSessionsMutation();
  const [confirming, setConfirming] = useState(false);

  const sessions = query.data?.sessions ?? [];
  const hasActive = sessions.some((session) => session.isActive);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-semibold text-ink">
            {t('admin.access.sessions.title')}
          </h3>
          <p className="text-xs text-inkFaint">{t('admin.access.sessions.retention')}</p>
        </div>
        {userId !== me && hasActive ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('admin.access.sessions.revokeAll')}
          </Button>
        ) : null}
      </div>

      {query.isPending ? (
        <TableSkeleton columns={5} />
      ) : query.isError ? (
        <EmptyState title={t('admin.access.loadError')} />
      ) : sessions.length === 0 ? (
        <EmptyState title={t('admin.access.sessions.empty')} />
      ) : (
        <Table caption={t('admin.access.sessions.caption')}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('admin.access.sessions.colOpened')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.access.sessions.colLastUsed')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.access.sessions.colIp')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.access.sessions.colBrowser')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.access.sessions.colState')}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.sessionId}>
                <TableCell>
                  <span className="text-xs text-inkFaint">
                    {new Date(session.createdAt).toLocaleString('fr-TN')}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-inkFaint">
                    {new Date(session.lastUsedAt).toLocaleString('fr-TN')}
                  </span>
                </TableCell>
                <TableCell>
                  {session.ipAddress === null ? (
                    <span className="text-xs text-inkFaint">
                      {t('admin.access.sessions.noMetadata')}
                    </span>
                  ) : (
                    <span className="font-mono text-xs text-ink">{session.ipAddress}</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="break-all text-xs text-inkSoft">{session.userAgent ?? '—'}</span>
                </TableCell>
                <TableCell>
                  {session.isActive ? (
                    <Badge tone="good">{t('admin.access.sessions.active')}</Badge>
                  ) : (
                    <div className="flex flex-col items-start gap-1">
                      <Badge tone="neutral">{t('admin.access.sessions.ended')}</Badge>
                      {session.revokedReason !== null &&
                      SESSION_REASON_LABEL_KEY[session.revokedReason] !== undefined ? (
                        <span className="text-xs text-inkFaint">
                          {t(
                            SESSION_REASON_LABEL_KEY[session.revokedReason] ??
                              'admin.access.sessions.ended',
                          )}
                        </span>
                      ) : null}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PasswordActionDialog
        open={confirming}
        title={t('admin.access.sessions.revokeTitle')}
        warning={t('admin.access.sessions.revokeWarning')}
        confirmLabel={t('merchant.common.confirm')}
        subject={label}
        onClose={() => {
          setConfirming(false);
        }}
        onConfirm={async ({ reason: motive, password }) => {
          const result = await mutation.mutateAsync({
            userId,
            reason: motive,
            confirmPassword: password,
          });
          toast.success(t('admin.access.sessions.revokedToast', { count: result.sessionsEnded }));
          setConfirming(false);
        }}
      />
    </section>
  );
}
